import { ITurnoGeoRepository } from './ITurnoGeoRepository';
import {
  OvertimeCandidate,
  Overtime,
  Modality,
  CreateModalityInput,
  UpdateModalityInput,
  OfficeNetwork,
  CreateOfficeNetworkInput,
  UpdateOfficeNetworkInput,
  TurnoGeoParameter,
  TurnoGeoSettings,
  HomeLocation,
  RegisterHomeInput,
  UpdateHomeLocationInput,
  MyNetwork,
  NetworkReading,
  SaveReadingInput,
  ListReadingsFilters,
  UserProfile,
  UpsertUserProfileInput,
  UserSchedule,
  SetUserScheduleInput,
  BulkAssignScheduleInput,
  CreateRecordInput,
  TurnoGeoRecord,
  ListRecordsFilters,
  PagedResult,
  MyDay,
  CycleState,
  RecordType,
  CloseOpenShiftInput,
  AdminCloseShiftInput,
  CorrectRecordInput,
  RecordAuditEntry,
  OpenShift,
  ShiftClosureLog,
  ListShiftClosureFilters,
  ScheduleException,
  SaveScheduleExceptionInput,
  RespondPingInput,
  RespondPingResult,
  LocationPing,
  ListPingsFilters,
  Inconsistency,
  ListInconsistenciesFilters,
  ReviewInconsistencyInput,
  InconsistencySummary,
  Novelty,
  SaveNoveltyInput,
  ListNoveltiesFilters,
  ReviewNoveltyInput,
  ImportRowInput,
  ImportPreview,
  ImportResultRow,
  Employee,
  CredentialImportResult,
  Label,
  PublicLabel,
  SaveLabelInput,
  LocationContext,
  AdminSetLocationInput,
  EmployeePatch,
  Holiday,
  RateFactor,
  SaveRateFactorInput,
  HoursReport,
  HoursReportQuery,
  MyWeek,
  ImportMarkRow,
  ImportMarkResult,
} from './types';

const MODALITIES_KEY = 'lcc.turnogeo.modalities';
const OFFICE_NETWORKS_KEY = 'lcc.turnogeo.officeNetworks';
const PARAMETERS_KEY = 'lcc.turnogeo.parameters';
const HOME_KEY = 'lcc.turnogeo.home';
const PROFILES_KEY = 'lcc.turnogeo.profiles';
const SCHEDULE_KEY = 'lcc.turnogeo.schedule';
const RECORDS_KEY = 'lcc.turnogeo.records';

const SEED_MODALITIES: Modality[] = [
  { id: crypto.randomUUID(), code: 'PRESENCIAL', name: 'Presencial', sortOrder: 1, isActive: true },
  { id: crypto.randomUUID(), code: 'REMOTO', name: 'Remoto', sortOrder: 2, isActive: true },
  { id: crypto.randomUUID(), code: 'FUERA_DE_RANGO', name: 'Fuera de rango', sortOrder: 3, isActive: true },
  { id: crypto.randomUUID(), code: 'PENDIENTE_APROBACION', name: 'Pendiente de aprobación', sortOrder: 4, isActive: true },
];

const p = (code: string, category: string, label: string, valueType: TurnoGeoParameter['valueType'], value: string, sortOrder: number, minValue: number | null = null, maxValue: number | null = null): TurnoGeoParameter => ({
  code, category, label, description: null, valueType, value, minValue, maxValue, sortOrder,
});
/** Mismos parametros que siembra 017_turnogeo_parametros_ubicaciones.sql (solo para el modo demo sin backend). */
const SEED_PARAMETERS: TurnoGeoParameter[] = [
  p('TIME_ZONE', 'GENERAL', 'Zona horaria', 'TEXT', 'America/Bogota', 10),
  p('REQUIRE_PHOTO', 'FOTO', 'Requerir foto al marcar', 'BOOL', 'false', 20),
  p('PHOTO_RETENTION_DAYS', 'FOTO', 'Días de retención de fotos', 'INT', '90', 30, 1, 3650),
  p('REQUIRE_GPS_PERMISSION', 'UBICACION', 'Exigir permiso de GPS para marcar', 'BOOL', 'false', 40),
  p('DEFAULT_GPS_RADIUS_METERS', 'UBICACION', 'Radio GPS por defecto (metros)', 'INT', '150', 50, 10, 5000),
  p('MIN_GPS_ACCURACY_METERS', 'UBICACION', 'Precisión GPS máxima aceptada (metros)', 'INT', '200', 60, 10, 2000),
  p('IPV6_PREFIX_LENGTH', 'RED', 'Prefijo IPv6 a comparar (bits)', 'INT', '64', 70, 16, 128),
  p('ALLOW_FALLBACK_CIDR', 'RED', 'Permitir respaldo por rango /24 de IPv4', 'BOOL', 'false', 80),
  p('REQUIRE_HOME_APPROVAL', 'CASA', 'Exigir aprobación del administrador al registrar casa u oficina', 'BOOL', 'false', 90),
  p('PROBE_IPV4_URL', 'RED', 'Dirección de sonda solo IPv4 (opcional)', 'TEXT', '', 100),
  p('PROBE_IPV6_URL', 'RED', 'Dirección de sonda solo IPv6 (opcional)', 'TEXT', '', 110),
  p('PROVIDER_ALIASES', 'RED', 'Nombres comerciales de proveedores de internet', 'TEXT', 'Telmex Colombia S.A.=Claro', 120),
  p('WORK_START_TIME', 'JORNADA', 'Hora general de entrada', 'TEXT', '08:00', 200),
  p('WORK_END_TIME', 'JORNADA', 'Hora general de salida', 'TEXT', '17:24', 210),
  p('LUNCH_START_TIME', 'JORNADA', 'Hora programada de salida a almorzar', 'TEXT', '12:00', 220),
  p('LUNCH_END_TIME', 'JORNADA', 'Hora programada de regreso del almuerzo', 'TEXT', '13:00', 230),
  p('LUNCH_MINUTES', 'JORNADA', 'Minutos de almuerzo que se descuentan de la jornada', 'INT', '60', 240, 0, 240),
  p('LUNCH_MAX_MINUTES', 'JORNADA', 'Máximo de minutos de almuerzo permitidos', 'INT', '70', 250, 0, 300),
  p('WORK_DAYS', 'JORNADA', 'Días laborados', 'TEXT', '1,2,3,4,5', 260),
  p('WEEKLY_HOURS', 'JORNADA', 'Horas semanales de la jornada ordinaria', 'INT', '42', 270, 1, 60),
  p('SCHEDULE_TOLERANCE_MINUTES', 'JORNADA', 'Tolerancia frente al horario (minutos)', 'INT', '10', 280, 0, 120),
  p('MAX_SHIFT_HOURS', 'CONTROL', 'Horas máximas de un turno abierto', 'INT', '14', 300, 4, 24),
  p('CLOCK_DRIFT_MINUTES', 'CONTROL', 'Desfase máximo permitido del reloj del celular (minutos)', 'INT', '3', 310, 0, 60),
  p('SHOW_ACCUMULATED_HOURS', 'CONTROL', 'Mostrar horas acumuladas a los empleados', 'BOOL', 'false', 320),
];

function load<T>(key: string, seed: T): T {
  try {
    const raw = localStorage.getItem(key);
    if (raw) return JSON.parse(raw) as T;
  } catch {
    // localStorage no disponible (ej. modo privado) - se sigue con el seed en memoria.
  }
  return seed;
}
function save<T>(key: string, value: T) {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch {
    // Ignorar: solo afecta la persistencia entre recargas en modo local/demo.
  }
}

/**
 * Repositorio LOCAL de TurnoGeo (modo demo, sin backend real - ver
 * shared/services/env.ts / VITE_DATA_MODE). Persiste en localStorage para
 * que la demo sobreviva recargas de pagina, igual que los demas modulos.
 */
export class LocalTurnoGeoRepository implements ITurnoGeoRepository {
  private modalities: Modality[] = load(MODALITIES_KEY, SEED_MODALITIES);
  private officeNetworks: OfficeNetwork[] = load(OFFICE_NETWORKS_KEY, []);
  private parameters: TurnoGeoParameter[] = load(PARAMETERS_KEY, SEED_PARAMETERS);
  private home: HomeLocation | null = load<HomeLocation | null>(HOME_KEY, null);
  private profiles: UserProfile[] = load(PROFILES_KEY, []);
  private schedule: UserSchedule[] = load(SCHEDULE_KEY, []);
  private records: TurnoGeoRecord[] = load(RECORDS_KEY, []);

  // Modalidad
  async listModalities(includeInactive = false): Promise<Modality[]> {
    return this.modalities.filter((m) => includeInactive || m.isActive).sort((a, b) => a.sortOrder - b.sortOrder);
  }
  async createModality(input: CreateModalityInput): Promise<Modality> {
    const row: Modality = { id: crypto.randomUUID(), code: input.code.toUpperCase(), name: input.name, sortOrder: input.sortOrder ?? 0, isActive: true };
    this.modalities.push(row);
    save(MODALITIES_KEY, this.modalities);
    return row;
  }
  async updateModality(id: string, patch: UpdateModalityInput): Promise<Modality> {
    const row = this.modalities.find((m) => m.id === id)!;
    Object.assign(row, patch);
    save(MODALITIES_KEY, this.modalities);
    return row;
  }
  async deleteModality(id: string): Promise<void> {
    const row = this.modalities.find((m) => m.id === id);
    if (row) row.isActive = false;
    save(MODALITIES_KEY, this.modalities);
  }

  // Redes de oficina
  async listOfficeNetworks(includeInactive = false): Promise<OfficeNetwork[]> {
    return this.officeNetworks.filter((n) => includeInactive || n.isActive);
  }
  async createOfficeNetwork(input: CreateOfficeNetworkInput): Promise<OfficeNetwork> {
    const row: OfficeNetwork = {
      id: crypto.randomUUID(),
      isActive: true,
      officeName: input.officeName,
      ips: input.ips.map((ip) => ({ id: crypto.randomUUID(), ipAddress: ip })),
      publicIpCidrFallback: input.publicIpCidrFallback ?? null,
      gpsLatitude: input.gpsLatitude ?? null,
      gpsLongitude: input.gpsLongitude ?? null,
      gpsRadiusMeters: input.gpsRadiusMeters ?? 150,
      networkKind: input.networkKind ?? 'DIRECTA',
      validateGps: input.validateGps ?? true,
      notes: input.notes ?? null,
      providerAsn: input.providerAsn ?? null,
      providerName: input.providerName ?? null,
      facilities: input.facilities ?? null,
      address: input.address ?? null,
      neighborhood: input.neighborhood ?? null,
      commune: input.commune ?? null,
      boundaries: input.boundaries ?? null,
      landmark: input.landmark ?? null,
      city: input.city ?? null,
      country: input.country ?? null,
      mapSearchUrl: input.mapSearchUrl ?? null,
      networkEquipment: input.networkEquipment ?? null,
      ipIsFixed: input.ipIsFixed ?? null,
    };
    this.officeNetworks.push(row);
    save(OFFICE_NETWORKS_KEY, this.officeNetworks);
    return row;
  }
  async updateOfficeNetwork(id: string, patch: UpdateOfficeNetworkInput): Promise<OfficeNetwork> {
    const row = this.officeNetworks.find((n) => n.id === id)!;
    const { ips, ...rest } = patch;
    Object.assign(row, rest);
    if (ips) row.ips = ips.map((ip) => ({ id: crypto.randomUUID(), ipAddress: ip }));
    save(OFFICE_NETWORKS_KEY, this.officeNetworks);
    return row;
  }
  async deleteOfficeNetwork(id: string): Promise<void> {
    const row = this.officeNetworks.find((n) => n.id === id);
    if (row) row.isActive = false;
    save(OFFICE_NETWORKS_KEY, this.officeNetworks);
  }

  // Parametros
  private setting<T>(code: string, parse: (v: string) => T): T {
    return parse(this.parameters.find((x) => x.code === code)?.value ?? '');
  }
  async getConfiguration(): Promise<TurnoGeoSettings> {
    return {
      timeZone: this.setting('TIME_ZONE', (v) => v || 'America/Bogota'),
      requirePhoto: this.setting('REQUIRE_PHOTO', (v) => v === 'true'),
      photoRetentionDays: this.setting('PHOTO_RETENTION_DAYS', Number),
      requireGpsPermission: this.setting('REQUIRE_GPS_PERMISSION', (v) => v === 'true'),
      defaultGpsRadiusMeters: this.setting('DEFAULT_GPS_RADIUS_METERS', Number),
      minGpsAccuracyMeters: this.setting('MIN_GPS_ACCURACY_METERS', Number),
      ipv6PrefixLength: this.setting('IPV6_PREFIX_LENGTH', Number),
      allowFallbackCidr: this.setting('ALLOW_FALLBACK_CIDR', (v) => v === 'true'),
      requireHomeApproval: this.setting('REQUIRE_HOME_APPROVAL', (v) => v === 'true'),
      probeIpv4Url: this.setting('PROBE_IPV4_URL', (v) => v),
      probeIpv6Url: this.setting('PROBE_IPV6_URL', (v) => v),
      providerAliases: this.setting('PROVIDER_ALIASES', (v) => v),
      workStartTime: this.setting('WORK_START_TIME', (v) => v || '08:00'),
      workEndTime: this.setting('WORK_END_TIME', (v) => v || '17:24'),
      lunchStartTime: this.setting('LUNCH_START_TIME', (v) => v || '12:00'),
      lunchEndTime: this.setting('LUNCH_END_TIME', (v) => v || '13:00'),
      lunchMinutes: this.setting('LUNCH_MINUTES', (v) => Number(v || 60)),
      lunchMaxMinutes: this.setting('LUNCH_MAX_MINUTES', (v) => Number(v || 70)),
      workDays: this.setting('WORK_DAYS', (v) => v || '1,2,3,4,5'),
      weeklyHours: this.setting('WEEKLY_HOURS', (v) => Number(v || 42)),
      scheduleToleranceMinutes: this.setting('SCHEDULE_TOLERANCE_MINUTES', (v) => Number(v || 10)),
      maxShiftHours: this.setting('MAX_SHIFT_HOURS', (v) => Number(v || 14)),
      clockDriftMinutes: this.setting('CLOCK_DRIFT_MINUTES', (v) => Number(v || 3)),
      showAccumulatedHours: this.setting('SHOW_ACCUMULATED_HOURS', (v) => v === 'true'),
      pingsMorningCount: this.setting('PINGS_MORNING_COUNT', (v) => Number(v || 2)),
      pingsAfternoonCount: this.setting('PINGS_AFTERNOON_COUNT', (v) => Number(v || 2)),
      pingResponseMinutes: this.setting('PING_RESPONSE_MINUTES', (v) => Number(v || 10)),
      openShiftGraceMinutes: this.setting('OPEN_SHIFT_GRACE_MINUTES', (v) => Number(v || 120)),
      overtimeMarginMinutes: this.setting('OVERTIME_MARGIN_MINUTES', (v) => Number(v || 10)),
      overtimeRequireApproval: this.setting('OVERTIME_REQUIRE_APPROVAL', (v) => v !== 'false'),
    };
  }
  async updateConfiguration(settings: Partial<TurnoGeoSettings>): Promise<TurnoGeoSettings> {
    const MAP: Record<keyof TurnoGeoSettings, string> = {
      timeZone: 'TIME_ZONE',
      requirePhoto: 'REQUIRE_PHOTO',
      photoRetentionDays: 'PHOTO_RETENTION_DAYS',
      requireGpsPermission: 'REQUIRE_GPS_PERMISSION',
      defaultGpsRadiusMeters: 'DEFAULT_GPS_RADIUS_METERS',
      minGpsAccuracyMeters: 'MIN_GPS_ACCURACY_METERS',
      ipv6PrefixLength: 'IPV6_PREFIX_LENGTH',
      allowFallbackCidr: 'ALLOW_FALLBACK_CIDR',
      requireHomeApproval: 'REQUIRE_HOME_APPROVAL',
      probeIpv4Url: 'PROBE_IPV4_URL',
      probeIpv6Url: 'PROBE_IPV6_URL',
      providerAliases: 'PROVIDER_ALIASES',
      workStartTime: 'WORK_START_TIME',
      workEndTime: 'WORK_END_TIME',
      lunchStartTime: 'LUNCH_START_TIME',
      lunchEndTime: 'LUNCH_END_TIME',
      lunchMinutes: 'LUNCH_MINUTES',
      lunchMaxMinutes: 'LUNCH_MAX_MINUTES',
      workDays: 'WORK_DAYS',
      weeklyHours: 'WEEKLY_HOURS',
      scheduleToleranceMinutes: 'SCHEDULE_TOLERANCE_MINUTES',
      maxShiftHours: 'MAX_SHIFT_HOURS',
      clockDriftMinutes: 'CLOCK_DRIFT_MINUTES',
      showAccumulatedHours: 'SHOW_ACCUMULATED_HOURS',
      pingsMorningCount: 'PINGS_MORNING_COUNT',
      pingsAfternoonCount: 'PINGS_AFTERNOON_COUNT',
      pingResponseMinutes: 'PING_RESPONSE_MINUTES',
      openShiftGraceMinutes: 'OPEN_SHIFT_GRACE_MINUTES',
      overtimeMarginMinutes: 'OVERTIME_MARGIN_MINUTES',
      overtimeRequireApproval: 'OVERTIME_REQUIRE_APPROVAL',
    };
    for (const [field, code] of Object.entries(MAP) as [keyof TurnoGeoSettings, string][]) {
      if (field in settings && settings[field] !== undefined) {
        const row = this.parameters.find((x) => x.code === code);
        if (row) row.value = String(settings[field]);
      }
    }
    save(PARAMETERS_KEY, this.parameters);
    return this.getConfiguration();
  }
  async listParameters(): Promise<TurnoGeoParameter[]> {
    return [...this.parameters].sort((a, b) => a.sortOrder - b.sortOrder);
  }
  async updateParameter(code: string, value: string): Promise<TurnoGeoParameter> {
    const row = this.parameters.find((x) => x.code === code)!;
    row.value = value;
    save(PARAMETERS_KEY, this.parameters);
    return row;
  }

  // Red / casa (modo demo: sin servidor no hay IP real que detectar)
  async getMyNetwork(): Promise<MyNetwork> {
    return { ip: null, family: null, comparedAs: null, suggestedEntry: null, prefixLength: 64, matchesOffice: null, home: this.home ? { id: this.home.id, status: this.home.status, matches: false } : null, ownOffice: null, network: { provider: null, providerRegistered: null, asn: null } };
  }
  async saveReading(_input: SaveReadingInput): Promise<NetworkReading> {
    throw new Error('El Registrador necesita el servidor (modo demo sin conexión no puede leer tu IP).');
  }
  async listMyReadings(): Promise<NetworkReading[]> {
    return [];
  }
  async listReadings(_filters: ListReadingsFilters): Promise<NetworkReading[]> {
    return [];
  }
  async getMyLocations(): Promise<HomeLocation[]> {
    return this.home ? [this.home] : [];
  }
  async registerMyHome(input: RegisterHomeInput): Promise<HomeLocation> {
    this.home = {
      id: this.home?.id ?? crypto.randomUUID(), userId: 'local-user', userFullName: 'Usuario Demo', locationKind: input.locationKind ?? 'CASA', publicIpAddress: input.saveIp === false ? null : '(modo demo)',
      gpsLatitude: input.gpsLatitude ?? null, gpsLongitude: input.gpsLongitude ?? null, gpsAccuracyMeters: input.gpsAccuracyMeters ?? null,
      gpsRadiusMeters: 150, status: 'APROBADA', reviewedBy: null, reviewedDate: null, updatedDate: null,
    };
    save(HOME_KEY, this.home);
    return this.home;
  }
  async listHomeLocations(): Promise<HomeLocation[]> {
    return this.home ? [this.home] : [];
  }
  async reviewHomeLocation(_id: string, status: 'APROBADA' | 'RECHAZADA'): Promise<HomeLocation> {
    this.home = { ...this.home!, status };
    save(HOME_KEY, this.home);
    return this.home;
  }
  async updateHomeLocation(_id: string, patch: UpdateHomeLocationInput): Promise<HomeLocation> {
    this.home = { ...this.home!, ...patch };
    save(HOME_KEY, this.home);
    return this.home;
  }
  async deleteHomeLocation(): Promise<void> {
    this.home = null;
    save(HOME_KEY, this.home);
  }

  // Perfiles
  async listUserProfiles(): Promise<UserProfile[]> {
    return this.profiles;
  }
  async upsertUserProfile(input: UpsertUserProfileInput): Promise<UserProfile> {
    let row = this.profiles.find((p) => p.userId === input.userId);
    if (row) {
      row.documentType = input.documentType;
      row.documentNumber = input.documentNumber;
    } else {
      row = { id: crypto.randomUUID(), userId: input.userId, userFullName: input.userId, documentType: input.documentType, documentNumber: input.documentNumber };
      this.profiles.push(row);
    }
    save(PROFILES_KEY, this.profiles);
    return row;
  }
  async deleteUserProfile(id: string): Promise<void> {
    this.profiles = this.profiles.filter((p) => p.id !== id);
    save(PROFILES_KEY, this.profiles);
  }

  // Horario semanal
  async listUserSchedule(userId?: string): Promise<UserSchedule[]> {
    return userId ? this.schedule.filter((s) => s.userId === userId) : this.schedule;
  }
  async setUserSchedule(input: SetUserScheduleInput): Promise<UserSchedule> {
    const modality = this.modalities.find((m) => m.id === input.modalityId)!;
    let row = this.schedule.find((s) => s.userId === input.userId && s.dayOfWeek === input.dayOfWeek);
    if (row) {
      row.modalityId = input.modalityId;
      row.modalityCode = modality.code;
      row.modalityName = modality.name;
    } else {
      row = { id: crypto.randomUUID(), userId: input.userId, dayOfWeek: input.dayOfWeek, modalityId: input.modalityId, modalityCode: modality.code, modalityName: modality.name };
      this.schedule.push(row);
    }
    save(SCHEDULE_KEY, this.schedule);
    return row;
  }
  async bulkAssignSchedule(input: BulkAssignScheduleInput): Promise<{ updated: number }> {
    for (const userId of input.userIds) {
      await this.setUserSchedule({ userId, dayOfWeek: input.dayOfWeek, modalityId: input.modalityId });
    }
    return { updated: input.userIds.length };
  }
  async deleteUserScheduleDay(id: string): Promise<void> {
    this.schedule = this.schedule.filter((s) => s.id !== id);
    save(SCHEDULE_KEY, this.schedule);
  }

  // Marcaje - clasificacion simplificada en el cliente (solo para el modo demo sin backend real).
  async createRecord(input: CreateRecordInput): Promise<TurnoGeoRecord> {
    if (this.setting('REQUIRE_PHOTO', (v) => v === 'true') && !input.photoDataUrl) {
      throw new Error('Esta organización requiere foto para marcar.');
    }
    const fueraDeRango = this.modalities.find((m) => m.code === 'FUERA_DE_RANGO')!;
    const row: TurnoGeoRecord = {
      id: crypto.randomUUID(),
      userId: 'local-user',
      userFullName: 'Usuario Demo',
      recordType: input.recordType,
      recordTimestamp: new Date().toISOString(),
      photoUrl: input.photoDataUrl ?? null,
      detectedPublicIp: null,
      detectedLatitude: input.detectedLatitude ?? null,
      detectedLongitude: input.detectedLongitude ?? null,
      gpsAccuracyMeters: input.gpsAccuracyMeters ?? null,
      locationPermissionDenied: input.locationPermissionDenied ?? false,
      matchedOfficeNetworkId: null,
      matchedOfficeNetworkName: null,
      modalityId: fueraDeRango.id,
      modalityCode: fueraDeRango.code,
      modalityName: fueraDeRango.name,
      modalityReason: 'Modo demo (sin backend real): no se evaluó ninguna sede.',
      matchedHomeLocationId: null,
      validations: [{ code: 'IP', label: 'IP', status: 'na', detail: 'Modo demo: sin servidor no se evalúa la IP.' }],
      userDocumentNumber: null,
      note: input.note ?? null,
      serverTimestamp: new Date().toISOString(),
      deviceTimestamp: input.deviceTimestamp ?? null,
      clockDriftSeconds: null,
      scheduleDeviationMinutes: null,
      deviationConfirmed: input.deviationConfirmed ?? false,
      isLateClose: false,
      isCorrected: false,
      originalTimestamp: null,
      correctedBy: null,
      correctedDate: null,
      correctionReason: null,
      declaredWorkMode: input.declaredWorkMode ?? null,
    };
    this.records.unshift(row);
    save(RECORDS_KEY, this.records);
    return row;
  }
  async listRecords(filters: ListRecordsFilters): Promise<PagedResult<TurnoGeoRecord>> {
    const page = filters.page ?? 1;
    const pageSize = filters.pageSize ?? 20;
    const items = this.records.slice((page - 1) * pageSize, page * pageSize);
    return { items, total: this.records.length, page, pageSize };
  }

  // --- Jornada (modo demo: versión mínima; el control real de secuencia, cierre y correcciones vive en el servidor) ---
  async getMyDay(): Promise<MyDay> {
    const cfg = await this.getConfiguration();
    const asc = [...this.records].reverse();
    let lastEntry = -1;
    asc.forEach((r, i) => { if (r.recordType === 'ENTRADA') lastEntry = i; });
    const cycle = lastEntry >= 0 ? asc.slice(lastEntry) : [];
    const has = (t: RecordType) => cycle.some((r) => r.recordType === t);
    let state: CycleState = cycle.length === 0 ? 'SIN_TURNO' : 'TRABAJANDO';
    if (has('SALIDA')) state = 'CERRADO';
    else if (has('ENTRADA_ALMUERZO')) state = 'ALMUERZO_TERMINADO';
    else if (has('SALIDA_ALMUERZO')) state = 'EN_ALMUERZO';
    const next: Record<CycleState, RecordType[]> = {
      SIN_TURNO: ['ENTRADA'], CERRADO: ['ENTRADA'], TRABAJANDO: ['SALIDA_ALMUERZO', 'SALIDA'], EN_ALMUERZO: ['ENTRADA_ALMUERZO'], ALMUERZO_TERMINADO: ['SALIDA'],
    };
    const now = Date.now();
    const entry = cycle[0] ? new Date(cycle[0].recordTimestamp).getTime() : null;
    const out = cycle.find((r) => r.recordType === 'SALIDA');
    const lunchOut = cycle.find((r) => r.recordType === 'SALIDA_ALMUERZO');
    const lunchIn = cycle.find((r) => r.recordType === 'ENTRADA_ALMUERZO');
    let worked = 0;
    if (entry !== null) {
      const end = out ? new Date(out.recordTimestamp).getTime() : lunchOut && !lunchIn ? new Date(lunchOut.recordTimestamp).getTime() : now;
      const lunch = lunchOut && lunchIn ? new Date(lunchIn.recordTimestamp).getTime() - new Date(lunchOut.recordTimestamp).getTime() : 0;
      worked = Math.max(0, Math.round((end - entry - lunch) / 1000));
    }
    const dow = new Date().getDay();
    return {
      serverNow: new Date(now).toISOString(),
      localDate: new Date(now).toLocaleDateString('en-CA'),
      dayOfWeek: dow,
      timeZone: cfg.timeZone,
      schedule: {
        isWorkDay: cfg.workDays.split(',').map(Number).includes(dow),
        start: cfg.workStartTime, end: cfg.workEndTime, lunchStart: cfg.lunchStartTime, lunchEnd: cfg.lunchEndTime, lunchMinutes: cfg.lunchMinutes,
        crossesMidnight: false, netMinutes: 0, source: 'GENERAL', exceptionReason: null,
      },
      state,
      allowedNext: next[state],
      marks: cycle.map((r) => ({ id: r.id, type: r.recordType, at: r.recordTimestamp, isLateClose: false, isCorrected: false, declaredWorkMode: r.declaredWorkMode ?? null })),
      clock: entry === null ? null : { workedSeconds: worked, running: !out && !(lunchOut && !lunchIn), lunchInProgress: !!lunchOut && !lunchIn && !out, lunchSeconds: null, lunchAutoDiscount: !lunchIn, closed: !!out },
      openShift: state === 'SIN_TURNO' || state === 'CERRADO' || entry === null ? null : { entryAt: new Date(entry).toISOString(), ageMinutes: Math.round((now - entry) / 60000), stale: false, maxShiftHours: cfg.maxShiftHours },
      toleranceMinutes: cfg.scheduleToleranceMinutes,
      clockDriftMinutes: cfg.clockDriftMinutes,
      showAccumulatedHours: cfg.showAccumulatedHours,
      pendingPing: null,
    };
  }
  private needsServer(): never {
    throw new Error('Esta función necesita el servidor (el modo demo sin conexión no la incluye).');
  }
  async revalidateRecords(): Promise<{ reviewed: number; changed: number }> { return this.needsServer(); }
  async closeOpenShift(_input: CloseOpenShiftInput): Promise<TurnoGeoRecord> { return this.needsServer(); }
  async adminCloseShift(_input: AdminCloseShiftInput): Promise<TurnoGeoRecord> { return this.needsServer(); }
  async correctRecord(_id: string, _input: CorrectRecordInput): Promise<TurnoGeoRecord> { return this.needsServer(); }
  async listRecordAudit(_id: string): Promise<RecordAuditEntry[]> { return []; }
  async listOpenShifts(): Promise<OpenShift[]> { return []; }
  async listShiftClosureLog(_filters: ListShiftClosureFilters): Promise<ShiftClosureLog[]> { return []; }
  async listScheduleExceptions(_userId?: string): Promise<ScheduleException[]> { return []; }
  async createScheduleException(_input: SaveScheduleExceptionInput): Promise<ScheduleException> { return this.needsServer(); }
  async updateScheduleException(_id: string, _input: SaveScheduleExceptionInput): Promise<ScheduleException> { return this.needsServer(); }
  async deleteScheduleException(_id: string): Promise<void> { return this.needsServer(); }
  async respondPing(_id: string, _input: RespondPingInput): Promise<RespondPingResult> { return this.needsServer(); }
  async listPings(_filters: ListPingsFilters): Promise<PagedResult<LocationPing>> { return { items: [], total: 0, page: 1, pageSize: 20 }; }
  async listInconsistencies(_filters: ListInconsistenciesFilters, _scan?: boolean): Promise<PagedResult<Inconsistency>> { return { items: [], total: 0, page: 1, pageSize: 20 }; }
  async inconsistencySummary(): Promise<InconsistencySummary> { return { pending: 0, byCode: [] }; }
  async reviewInconsistency(_id: string, _input: ReviewInconsistencyInput): Promise<Inconsistency> { return this.needsServer(); }
  async listNovelties(_filters?: ListNoveltiesFilters): Promise<Novelty[]> { return []; }
  async createNovelty(_input: SaveNoveltyInput): Promise<Novelty> { return this.needsServer(); }
  async reviewNovelty(_id: string, _input: ReviewNoveltyInput): Promise<Novelty> { return this.needsServer(); }
  async deleteNovelty(_id: string): Promise<void> { return this.needsServer(); }
  async listLabels(): Promise<PublicLabel[]> { return []; }
  async listAllLabels(): Promise<Label[]> { return []; }
  async createLabel(_input: SaveLabelInput): Promise<Label> { return this.needsServer(); }
  async updateLabel(_id: string, _input: SaveLabelInput): Promise<Label> { return this.needsServer(); }
  async deleteLabel(_id: string): Promise<void> { return this.needsServer(); }
  async getMyLocationContext(): Promise<LocationContext> { return { places: [], sedes: [], minGpsAccuracyMeters: 200, hasReference: false, requireApproval: false }; }
  async adminSetLocation(_input: AdminSetLocationInput): Promise<HomeLocation> { return this.needsServer(); }
  async updateEmployee(_userId: string, _patch: EmployeePatch): Promise<Employee> { return this.needsServer(); }
  async listHolidays(): Promise<Holiday[]> { return []; }
  async createHoliday(_i: { date: string; name: string }): Promise<Holiday> { return this.needsServer(); }
  async updateHoliday(_id: string, _p: { name?: string; isActive?: boolean }): Promise<Holiday> { return this.needsServer(); }
  async deleteHoliday(_id: string): Promise<void> { return this.needsServer(); }
  async generateHolidays(_y: number): Promise<{ created: number; existing: number }> { return this.needsServer(); }
  async listRateFactors(): Promise<RateFactor[]> { return []; }
  async createRateFactor(_i: SaveRateFactorInput): Promise<RateFactor> { return this.needsServer(); }
  async updateRateFactor(_id: string, _i: SaveRateFactorInput): Promise<RateFactor> { return this.needsServer(); }
  async deleteRateFactor(_id: string): Promise<void> { return this.needsServer(); }
  async getHoursReport(_q: HoursReportQuery): Promise<HoursReport> { return this.needsServer(); }
  async getOvertimeCandidates(): Promise<OvertimeCandidate[]> { return []; }
  async requestOvertime(): Promise<Overtime> { return this.needsServer(); }
  async declineOvertime(): Promise<Overtime> { return this.needsServer(); }
  async listMyOvertime(): Promise<Overtime[]> { return []; }
  async listOvertime(): Promise<Overtime[]> { return this.needsServer(); }
  async reviewOvertime(): Promise<Overtime> { return this.needsServer(); }
  async sendOvertimeToHr(): Promise<{ sent: number }> { return this.needsServer(); }
  async getMyWeek(): Promise<MyWeek> { return { visible: false, weekStart: '', weekEnd: '', netHours: 0, minHours: 42 }; }
  async importMarks(_r: ImportMarkRow[]): Promise<ImportMarkResult[]> { return this.needsServer(); }
  async getRecordPhotoUrl(_id: string): Promise<string> { return this.needsServer(); }
  async listCredentialUserIds(): Promise<string[]> { return []; }
  async getInitialPassword(_userId: string): Promise<string | null> { return null; }
  async setInitialPassword(_userId: string, _password: string): Promise<void> { return this.needsServer(); }
  async importInitialPasswords(_rows: { email: string; password: string }[]): Promise<CredentialImportResult[]> { return this.needsServer(); }
  async listEmployees(): Promise<Employee[]> { return []; }
  async previewEmployeeImport(_rows: ImportRowInput[]): Promise<ImportPreview> { return this.needsServer(); }
  async executeEmployeeImport(_rows: ImportRowInput[], _startIndex: number): Promise<ImportResultRow[]> { return this.needsServer(); }
}
